/* eslint-disable import/prefer-default-export */
import { clone } from 'lodash';

import { FIELD_TYPE_CHECKBOX } from 'SharedDir/constants/field-types';
import { Field, FieldValue } from 'SharedDir/declarations/fields';
import { NIssue } from 'SharedDir/declarations/issue';

const YOUTUBE_VIDEO_ID = /^[A-Za-z0-9_-]{11}$/;
const RUTUBE_VIDEO_ID = /^[A-Za-z0-9-]+$/;
const VK_VIDEO_ID = /^-?\d+_\d+$/;

const getUrlFromString = (link: string): URL | null => {
  try {
    return new URL(link);
  } catch {
    return null;
  }
};

const getYouTubeEmbedLink = (url: URL): string | null => {
  const hostname = url.hostname.replace(/^www\./, '');
  let videoId = '';

  if (hostname === 'youtu.be') {
    videoId = url.pathname.slice(1);
  }

  if (hostname === 'youtube.com') {
    if (url.pathname === '/watch') {
      videoId = url.searchParams.get('v') || '';
    } else if (url.pathname.startsWith('/embed/')) {
      videoId = url.pathname.split('/')[2] || '';
    }
  }

  if (!YOUTUBE_VIDEO_ID.test(videoId)) {
    return null;
  }

  return `https://www.youtube.com/embed/${videoId}`;
};

const getRuTubeEmbedLink = (url: URL): string | null => {
  const hostname = url.hostname.replace(/^www\./, '');
  const pathParts = url.pathname.split('/').filter(Boolean);

  if (hostname !== 'rutube.ru') {
    return null;
  }

  let videoId = '';
  if (pathParts[0] === 'video') {
    videoId = pathParts[1] || '';
  } else if (pathParts[0] === 'play' && pathParts[1] === 'embed') {
    videoId = pathParts[2] || '';
  }

  if (!RUTUBE_VIDEO_ID.test(videoId)) {
    return null;
  }

  return `https://rutube.ru/play/embed/${videoId}`;
};

const getVkEmbedLink = (url: URL): string | null => {
  const hostname = url.hostname.replace(/^www\./, '');

  if (!['vk.com', 'vkvideo.ru'].includes(hostname)) {
    return null;
  }

  if (url.pathname === '/video_ext.php') {
    const oid = url.searchParams.get('oid');
    const id = url.searchParams.get('id');

    if (!oid || !id) {
      return null;
    }

    const embedUrl = new URL('https://vk.com/video_ext.php');
    url.searchParams.forEach((value, key) => {
      embedUrl.searchParams.set(key, value);
    });
    return embedUrl.toString();
  }

  const videoIdMatch = url.pathname.match(/\/video(?<VIDEO_ID>-?\d+_\d+)/);
  const videoId = videoIdMatch?.groups?.VIDEO_ID || '';
  if (!VK_VIDEO_ID.test(videoId)) {
    return null;
  }

  const [oid, id] = videoId.split('_');
  const embedUrl = new URL('https://vk.com/video_ext.php');
  embedUrl.searchParams.set('oid', oid);
  embedUrl.searchParams.set('id', id);
  return embedUrl.toString();
};

const getVideoEmbedLink = (link: string): string | null => {
  const url = getUrlFromString(link);

  if (!url) {
    return null;
  }

  return getYouTubeEmbedLink(url) || getRuTubeEmbedLink(url) || getVkEmbedLink(url);
};

/**
 * Валидация ссылки на видео YouTube, RuTube или VK Видео.
 *
 * @param link
 */
export const getVideoLinkValidationError = (link: string): string | null => {
  if (!link) {
    return null;
  }

  const url = getUrlFromString(link);

  if (!url) {
    return 'Укажите корректную ссылку на видео';
  }

  const hostname = url.hostname.replace(/^www\./, '');

  if (['youtube.com', 'youtu.be'].includes(hostname)) {
    return getYouTubeEmbedLink(url) ? null : 'Некорректная ссылка на YouTube видео';
  }

  if (hostname === 'rutube.ru') {
    return getRuTubeEmbedLink(url) ? null : 'Некорректная ссылка на RuTube видео';
  }

  if (['vk.com', 'vkvideo.ru'].includes(hostname)) {
    return getVkEmbedLink(url) ? null : 'Некорректная ссылка на VK Видео';
  }

  return 'Поддерживаются только ссылки на YouTube, RuTube и VK Видео';
};

export const videoLinkValidate = (link: string): boolean => !getVideoLinkValidationError(link);

/**
 * Преобразуем поддерживаемую ссылку на видео в embed-ссылку.
 *
 * @param link
 */
export const videoEmbedifier = (link: string): string => getVideoEmbedLink(link) || link;

export const toStandardPhone = (phone: string): string => {
  const phonePattern = /(\+7|8)(\d{3})(\d{3})(\d{2})(\d{2})/;
  return phone.replace(phonePattern, '+7 ($2) $3-$4-$5');
};

export const makeDefaultValueBySectionField = (sectionField: NIssue.SectionFields): FieldValue => {
  let value: unknown = null;

  if (sectionField.field.fieldTypeId === FIELD_TYPE_CHECKBOX.id) {
    value = false;
  }

  const emptyValue = sectionField.params.default ?? { value };

  return emptyValue;
};

/**
 * делаем пустой объект значений полей
 *
 * @param fields
 */
export const makeFieldValuesEmptyObject = (
  sectionFields: Array<NIssue.SectionFields>
): Record<number, FieldValue> => {
  const values = {};
  sectionFields.forEach((sectionField) => {
    const emptyValue = makeDefaultValueBySectionField(sectionField);

    values[sectionField.id] = clone(emptyValue);
  });

  return values;
};
